import { atom } from "jotai";
import { Node, Edge } from "@xyflow/react";
import { isViewOnlyModeAtom } from "../permissions";
import { canvasAtom } from "../canvas";
import { processEdgesBeforeSave, processNodesBeforeSave } from "../utils";
import { createNotificationSetter } from "../notifications";
import { Canvas } from "@zapier/types/src/Canvas";
import { OutgoingBackendEdge, OutgoingBackendNode } from "../types";
import { isDraftModeIncludingTemplatesAtom } from "../nodes";
import { shouldBlockPersistenceAtom } from "./shouldBlockPersistenceAtom";
import { allowPersistenceInViewOnlyModeAtom } from "./allowPersistenceInViewOnlyModeAtom";

type Params = {
  nodes?: Node[];
  edges?: Edge[];
  name?: string;
  is_public?: boolean;
  description?: string;
};

type CanvasToSave = Omit<Canvas, "nodes" | "edges"> & {
  nodes: OutgoingBackendNode[];
  edges: OutgoingBackendEdge[];
};

type SaveCanvasResult = {
  success: boolean;
  error?: string;
  statusCode?: number;
};

const GENERIC_ERROR_MSG = "There was an issue saving your canvas.";

export const saveCanvasSetter = atom(
  null,
  async (
    get,
    set,
    updatedCanvas: Params,
  ): Promise<SaveCanvasResult | undefined> => {
    const canvas = get(canvasAtom);
    // See CanvasHydrator for how isViewOnlyMode is calculated on initial page load
    const isViewOnlyMode = get(isViewOnlyModeAtom);
    const shouldBlockPersistence = get(shouldBlockPersistenceAtom);
    const allowPersistenceInViewOnlyMode = get(
      allowPersistenceInViewOnlyModeAtom,
    );
    const isDraftModeIncludingTemplates = get(
      isDraftModeIncludingTemplatesAtom,
    );

    /** We don't want to make any API calls from templates page, or when we have
     * zsl from AI, or when we're in view-only mode
     * */
    if (
      !canvas.id ||
      isDraftModeIncludingTemplates ||
      shouldBlockPersistence ||
      (isViewOnlyMode && !allowPersistenceInViewOnlyMode)
    ) {
      return { success: true }; // No-op is considered success
    }

    const { nodes, edges, ...rest } = updatedCanvas;
    const canvasToSave: Partial<CanvasToSave> = { ...rest };

    /* Ensures that nodes and edges are only included in the PATCH request when they are defined. If either nodes or edges are undefined, they will be omitted from the request body, so we don't accidentally overwrite the current nodes and edges.*/
    if (nodes) {
      canvasToSave.nodes = processNodesBeforeSave(nodes);
    }

    if (edges) {
      canvasToSave.edges = processEdgesBeforeSave(edges);
    }

    try {
      const res = await fetch(`/api/canvas/v2/canvases/${canvas.id}/`, {
        method: "PATCH",
        headers: {
          "Content-type": "application/json",
        },
        body: JSON.stringify(canvasToSave),
      });

      if (!res.ok) {
        const errorMessage =
          res.status === 403 // User lost permission while they were actively on a canvas
            ? "You do not have permission to edit this canvas."
            : res.status === 401
              ? "You need to log in to edit this canvas."
              : GENERIC_ERROR_MSG;

        set(createNotificationSetter, {
          type: "error",
          message: errorMessage,
        });

        if (res.status === 403 || res.status === 401) {
          setTimeout(
            () =>
              (window.location.href = `/app/login?next=/app/canvas/${canvas.id}`),
            2500,
          );
        }

        return {
          success: false,
          error: errorMessage,
          statusCode: res.status,
        };
      }

      return { success: true };
    } catch (err: unknown) {
      console.error("Error saving canvas", err);

      set(createNotificationSetter, {
        type: "error",
        message: GENERIC_ERROR_MSG,
      });

      return {
        success: false,
        error: err instanceof Error ? err.message : GENERIC_ERROR_MSG,
      };
    }
  },
);

saveCanvasSetter.debugLabel = "saveCanvasSetter";
